import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { asyncConnect } from 'react-async-client';
import { Row, Col } from 'antd';
import { head, keys, propOr } from 'ramda';
import Translate from 'react-translate-component';
import cx from 'classnames';

import { getEducationTimesheet, putTrainerMarks } from 'actions/asyncActions';
import { fullAutoOptions } from 'constants/asyncClient';
import { DATE_FORMATS } from 'constants/dateFormats';
import { openModalTrainerMark } from 'actions/modalActions';
import { openState } from 'constants/scenario';

import RefreshButton from 'components/common/controls/RefreshButton';
import withTranslateChange from 'components/hocs/withTranslateChange';
import { DateRangeField, TimeRangeField } from 'components/common/fields/DateTimeFields';
import StaticTable from 'components/common/tableComponents/StaticTable';
import RangePicker from 'components/common/controls/RangePicker';

const getFirstProp = (obj) => propOr({}, head(keys(obj)), obj);

const Attended = ({ mark = '' }) => (
    <div className="attended">
        <div className="mark">
            {mark}
        </div>
    </div>
);

const Missed = ({ mark }) => (
    <div className="missed">
        { mark || <Translate content="education.trainer.missed" /> }
    </div>
);

const NoData = () => <div className="no-presence-data"/>;

const PRESENCE_STATES = {
    0: Missed,
    1: Attended,
    2: NoData
};

const MarkCell = ({ onClick, mark, presence }) => {
    const MarkComponent = PRESENCE_STATES[presence] || <div className="trainer-mark-input" />;

    return (
        <div onClick={onClick}>
            <MarkComponent mark={mark} />
        </div>
    );
};

const TRAINER_TIMESHEET_PARAMS = {
    participant: 0,
    trainer: 1
};

@asyncConnect({
    getEducationTimesheet: getEducationTimesheet
        .withDefaultPayload(({ dateto, datefrom, project }) => ({
            dateto: dateto && dateto.format(DATE_FORMATS.parse),
            datefrom: datefrom && datefrom.format(DATE_FORMATS.parse),
            project,
            ...TRAINER_TIMESHEET_PARAMS
        }))
        .withOptions(fullAutoOptions),
    putTrainerMarks: putTrainerMarks.withSuccessHandler(({ getEducationTimesheet: { refresh } }) => refresh())
}, null, { openModalTrainerMark })
@withTranslateChange(({ getEducationTimesheet: { refresh } }) => refresh())
export default class TrainerTimetableTable extends Component {
    static propTypes = {
        project: PropTypes.string.isRequired,
        dateto: PropTypes.object,
        datefrom: PropTypes.object,
        getEducationTimesheet: PropTypes.object.isRequired,
        setDateRange: PropTypes.func.isRequired,
        openModalTrainerMark: PropTypes.func.isRequired
    }

    getTableColumns = () => {
        const { getEducationTimesheet: { data, meta } } = this.props;

        return meta.lastSucceedAt ? [
            {
                name: 'number',
                width: 60,
                additionalClass: 'p-l-15',
                template: ({ rowIndex }) => rowIndex + 1
            },
            {
                name: 'participant',
                label: <Translate content="education.trainer.participants" />,
                width: 260,
                template: ({ item }) => (
                    <span className="participant">
                        {getFirstProp(item).participantName}
                    </span>
                )
            },
            ...data.classes.map(({ dt: { date, time }, name, id, state }) => ({
                name,
                width: 260,
                label: (
                    <div className="label">
                        <h3 className={cx({ 'finished': state !== openState })}>{name}</h3>
                        <h4>
                            <TimeRangeField time={time} hideEmpty={true} /> <DateRangeField date={date} />
                        </h4>
                    </div>
                ),
                additionalClass: 'paddings-items max-width-260',
                template: ({ item }) => {
                    const { mark, presence, participantId } = item[id] || {};

                    const modalParams = {
                        eventId: id,
                        eventName: name,
                        item: item[id]
                    };

                    if (participantId) {
                        return (
                            <MarkCell
                                mark={mark}
                                onClick={() => this.props.openModalTrainerMark(modalParams)}
                                presence={presence}
                            />
                        );
                    }

                    return <div className="not-related" />;
                }
            }))
        ] : [];
    }

    render() {
        const { getEducationTimesheet: { data, meta, refresh }, datefrom, dateto, setDateRange } = this.props;
        const value = [datefrom, dateto];

        return (
            <div className="trainer-timetable">
                <Row className="react-table-controls list-controls">
                    <Col span={14} className='range-picker m-t-10'>
                        <RangePicker allowClear={false} setDateRange={setDateRange} value={value} />
                    </Col>
                    <Col span={10} className="text-right icon-bar">
                        <RefreshButton refreshMeta={meta} handleRefresh={refresh} />
                    </Col>
                </Row>
                <StaticTable
                    meta={meta}
                    refresh={refresh}
                    items={data.items}
                    className="width-auto-table"
                    lockedColumns={2}
                    columns={this.getTableColumns()}
                />
            </div>
        )
    }
}
